import React, { useState, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faCopy, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { backendUrl } from '../utils/variables';
import { useCustomAlert } from './CustomAlert';
import { MyContext } from '../MainContext';

const GeneratePublicUrlModal = ({ groupId, groupName, onClose, onUrlGenerated, existingPublicUrl }) => {
    const { user } = useContext(MyContext);
    const { showAlert, AlertComponent } = useCustomAlert();
    const [loading, setLoading] = useState(false);
    const [publicUrl, setPublicUrl] = useState(existingPublicUrl || '');
    const [fullUrl, setFullUrl] = useState(existingPublicUrl ? `https://clowza.com/group/${existingPublicUrl}` : '');

    const generatePublicUrl = async () => {
        try {
            setLoading(true);
            
            if (!user || !user.user_id) {
                showAlert('error', 'User not authenticated. Please log in again.');
                return;
            }
            
            const userId = user.user_id;
            console.log('Generating public URL for group:', groupId, 'user:', userId);
            
            const response = await fetch(`${backendUrl}/generate_group_public_url.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: userId
                })
            });

            console.log('Response status:', response.status);
            const data = await response.json();
            console.log('Response data:', data);
            
            if (data.success) {
                setPublicUrl(data.public_url);
                setFullUrl(data.full_url);
                showAlert('success', 'Public URL generated successfully!');
                if (onUrlGenerated) {
                    onUrlGenerated(data.public_url);
                }
            } else {
                console.error('API Error:', data.error);
                showAlert('error', data.error || 'Failed to generate public URL');
            }
        } catch (err) {
            console.error('Network/Parse Error:', err);
            showAlert('error', 'Failed to generate public URL: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            showAlert('success', 'URL copied to clipboard!');
        } catch (err) {
            showAlert('error', 'Failed to copy URL');
        }
    };

    const openInNewTab = () => {
        window.open(fullUrl, '_blank');
    };

    return (
        <>
            <div className="modal-overlay">
                <div className="modal-content generate-url-modal">
                    <div className="modal-header">
                        <h3>
                            <FontAwesomeIcon icon={faLink} className="me-2" />
                            Generate Public URL
                        </h3>
                        <button className="close-btn" onClick={onClose}>×</button>
                    </div>
                    <div className="modal-body">
                        <div className="group-info">
                            <h4>{groupName}</h4>
                            <p>Create a public URL that allows people to discover and request to join this group.</p>
                        </div>

                        {!publicUrl ? (
                            <div className="generate-section">
                                <div className="info-box">
                                    <h5>What happens when you generate a public URL?</h5>
                                    <ul>
                                        <li>People can visit the URL without logging in</li>
                                        <li>They can see basic group information</li>
                                        <li>They can request to join the group</li>
                                        <li>You'll receive join requests to approve or reject</li>
                                    </ul>
                                </div>
                                
                                <button 
                                    className="btn btn-primary generate-btn"
                                    onClick={generatePublicUrl}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <div className="spinner me-2"></div>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faLink} className="me-2" />
                                            Generate Public URL
                                        </>
                                    )}
                                </button>
                            </div>
                        ) : (
                            <div className="url-section">
                                <div className="success-message">
                                    <FontAwesomeIcon icon={faLink} className="text-success me-2" />
                                    <strong>Public URL generated successfully!</strong>
                                </div>
                                
                                <div className="url-display">
                                    <label>Public URL:</label>
                                    <div className="url-input-group">
                                        <input 
                                            type="text" 
                                            value={fullUrl} 
                                            readOnly 
                                            className="form-control url-input"
                                        />
                                        <div className="url-input-group my-2">
                                            <button 
                                                className="btn btn-outline-secondary copy-btn"
                                                onClick={() => copyToClipboard(fullUrl)}
                                                title="Copy URL"
                                            >
                                                <FontAwesomeIcon icon={faCopy} />
                                            </button>
                                            <button 
                                                className="btn btn-outline-primary preview-btn"
                                                onClick={openInNewTab}
                                                title="Preview in new tab"
                                            >
                                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="url-info">
                                    <h6>Share this URL to:</h6>
                                    <ul>
                                        <li>Invite new members to your group</li>
                                        <li>Allow people to discover your group</li>
                                        <li>Enable public join requests</li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>
                            {publicUrl ? 'Close' : 'Cancel'}
                        </button>
                    </div>
                </div>
            </div>
            <AlertComponent />
        </>
    );
};

export default GeneratePublicUrlModal;
